/**
 * BUSO Graph – loads Chart.js (MIT) once, builds charts from JSON config scripts.
 * Supports one or two series (merged X categories).
 */
(function () {
  var CHART_SRC = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js';
  var chartLoadPromise = null;

  function loadChartJs() {
    if (window.Chart) return Promise.resolve();
    if (chartLoadPromise) return chartLoadPromise;
    chartLoadPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = CHART_SRC;
      s.async = true;
      s.onload = function () {
        resolve();
      };
      s.onerror = function () {
        chartLoadPromise = null;
        reject(new Error('Chart.js failed to load'));
      };
      document.head.appendChild(s);
    });
    return chartLoadPromise;
  }

  function parseRgbTriplet(str) {
    if (!str) return null;
    var t = String(str).trim();
    if (!t) return null;
    var parts = t.split(/[\s,]+/).filter(Boolean);
    if (parts.length >= 3) {
      return parts.slice(0, 3).join(', ');
    }
    return null;
  }

  function foregroundFromCanvas(canvas) {
    var el = canvas.closest('[class*="color-"]') || canvas.parentElement;
    if (!el) return '80, 80, 80';
    var raw = getComputedStyle(el).getPropertyValue('--color-foreground').trim();
    return parseRgbTriplet(raw) || '80, 80, 80';
  }

  function buttonColorFromCanvas(canvas) {
    var el = canvas.closest('[class*="color-"]') || canvas.parentElement;
    if (!el) return null;
    var raw = getComputedStyle(el).getPropertyValue('--color-button').trim();
    return parseRgbTriplet(raw);
  }

  function parseConfig(scriptEl) {
    try {
      return JSON.parse(scriptEl.textContent);
    } catch (e) {
      return null;
    }
  }

  function normalizeY(v) {
    if (typeof v === 'number' && !isNaN(v)) return v;
    var n = parseFloat(String(v).replace(',', '.'));
    return isNaN(n) ? null : n;
  }

  function normalizeValues(values) {
    return (values || []).map(function (v) {
      var n = normalizeY(v);
      return n == null ? 0 : n;
    });
  }

  function buildSeriesFromPairedPoints(pairedPoints, seriesLabels) {
    var labels = [];
    var s1 = [];
    var s2 = [];
    (pairedPoints || []).forEach(function (p) {
      var x = String(p && p.x != null ? p.x : '');
      if (!x) return;
      labels.push(x);
      s1.push(normalizeY(p.y1));
      s2.push(normalizeY(p.y2));
    });

    var datasetsMeta = [];
    if (s1.some(function (v) { return v != null; })) {
      datasetsMeta.push({
        key: '1',
        label: (seriesLabels && seriesLabels['1']) || 'Series 1',
        data: s1
      });
    }
    if (s2.some(function (v) { return v != null; })) {
      datasetsMeta.push({
        key: '2',
        label: (seriesLabels && seriesLabels['2']) || 'Series 2',
        data: s2
      });
    }

    return { labels: labels, datasetsMeta: datasetsMeta };
  }

  function buildMergedSeries(points, seriesOrder, seriesLabels) {
    var labelOrder = [];
    var seen = Object.create(null);
    (points || []).forEach(function (p) {
      var x = String(p.x == null ? '' : p.x);
      if (x === '') return;
      if (!seen[x]) {
        seen[x] = true;
        labelOrder.push(x);
      }
    });

    var maps = {};
    seriesOrder.forEach(function (key) {
      maps[key] = Object.create(null);
    });

    (points || []).forEach(function (p) {
      var g = String(p.group == null ? '1' : p.group);
      if (!maps[g]) return;
      var x = String(p.x == null ? '' : p.x);
      if (x === '') return;
      var y = normalizeY(p.y);
      if (y != null) maps[g][x] = y;
    });

    var datasetsMeta = [];
    seriesOrder.forEach(function (key, idx) {
      var label = (seriesLabels && seriesLabels[key]) || (idx === 0 ? 'Series 1' : 'Series 2');
      var data = labelOrder.map(function (x) {
        return maps[key][x] != null ? maps[key][x] : null;
      });
      var hasAny = data.some(function (v) {
        return v != null;
      });
      if (hasAny) {
        datasetsMeta.push({ key: key, label: label, data: data });
      }
    });

    return { labels: labelOrder, datasetsMeta: datasetsMeta };
  }

  function renderOne(cfg) {
    var canvas = document.getElementById(cfg.canvasId);
    if (!canvas || canvas.dataset.busoGraphRendered === '1') return;

    if (cfg.pairedPoints && cfg.pairedPoints.length) {
      renderMultiSeriesFromPaired(cfg, canvas);
      return;
    }

    if (cfg.points && cfg.points.length) {
      renderMultiSeries(cfg, canvas);
      return;
    }

    var labels = cfg.labels || [];
    var values = normalizeValues(cfg.values || []);
    if (!labels.length || !values.length) {
      var wrapEmpty = canvas.closest('.buso-graph__canvas-wrap');
      if (wrapEmpty) wrapEmpty.classList.add('buso-graph__canvas-wrap--empty');
      return;
    }

    canvas.dataset.busoGraphRendered = '1';
    var fg = foregroundFromCanvas(canvas);
    var border = 'rgb(' + fg + ')';
    var fill = cfg.chartType !== 'bar' && cfg.fill;
    var fillColor = fill ? 'rgba(' + fg + ', 0.12)' : cfg.chartType === 'bar' ? 'rgba(' + fg + ', 0.35)' : border;

    var ctx = canvas.getContext('2d');
    var type = cfg.chartType === 'bar' ? 'bar' : 'line';

    var dataset = {
      label: cfg.datasetLabel || cfg.series1Label || '',
      data: values,
      borderColor: border,
      backgroundColor: fillColor,
      tension: type === 'line' ? 0.25 : 0,
      fill: type === 'line' ? !!fill : false,
      borderWidth: type === 'bar' ? 0 : 2,
      pointRadius: type === 'line' ? 4 : 0,
      pointHoverRadius: type === 'line' ? 6 : 0
    };

    var yScale = makeYScale(cfg, fg, border);
    var chart = new Chart(ctx, {
      type: type,
      data: {
        labels: labels,
        datasets: [dataset]
      },
      options: makeChartOptions(cfg, fg, border, type, 1)
    });

    canvas.__busoGraphChart = chart;
  }

  function makeYScale(cfg, fg, border) {
    var yScale = {
      title: {
        display: !!(cfg.yAxisLabel && String(cfg.yAxisLabel).trim()),
        text: cfg.yAxisLabel || '',
        color: border
      },
      grid: { color: 'rgba(' + fg + ', 0.12)' },
      ticks: { color: border, maxTicksLimit: 20 },
      beginAtZero: cfg.beginAtZero !== false
    };
    if (cfg.yMax != null && cfg.yMax !== '' && !isNaN(Number(cfg.yMax))) {
      yScale.max = Number(cfg.yMax);
    }
    return yScale;
  }

  function makeChartOptions(cfg, fg, border, type, datasetCount) {
    var hasLegendLabel =
      (cfg.series1Label && String(cfg.series1Label).trim()) ||
      (cfg.series2Label && String(cfg.series2Label).trim()) ||
      (cfg.datasetLabel && String(cfg.datasetLabel).trim());
    var legendOn = !!cfg.showLegend && (datasetCount > 1 || !!hasLegendLabel);

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 450 },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          display: legendOn,
          labels: { color: border }
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.82)',
          titleColor: '#fff',
          bodyColor: '#fff',
          filter: function (item) {
            return item.parsed.y != null;
          }
        }
      },
      scales: {
        x: {
          title: {
            display: !!(cfg.xAxisLabel && String(cfg.xAxisLabel).trim()),
            text: cfg.xAxisLabel || '',
            color: border
          },
          grid: { color: 'rgba(' + fg + ', 0.08)' },
          ticks: {
            color: border,
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 20
          }
        },
        y: makeYScale(cfg, fg, border)
      }
    };
  }

  function renderMultiSeries(cfg, canvas) {
    var seriesLabels = {
      1: cfg.series1Label || 'Series 1',
      2: cfg.series2Label || 'Series 2'
    };
    var merged = buildMergedSeries(cfg.points, ['1', '2'], seriesLabels);
    if (!merged.labels.length || !merged.datasetsMeta.length) {
      var wrapEmpty = canvas.closest('.buso-graph__canvas-wrap');
      if (wrapEmpty) wrapEmpty.classList.add('buso-graph__canvas-wrap--empty');
      return;
    }

    canvas.dataset.busoGraphRendered = '1';
    var fg = foregroundFromCanvas(canvas);
    var fgRgb = 'rgb(' + fg + ')';
    var btn = buttonColorFromCanvas(canvas);
    var secRgb = btn ? 'rgb(' + btn + ')' : fgRgb;
    var secTriplet = btn || fg;

    var ctx = canvas.getContext('2d');
    var type = cfg.chartType === 'bar' ? 'bar' : 'line';

    var datasets = merged.datasetsMeta.map(function (meta, idx) {
      var isFirst = meta.key === '1' || idx === 0;
      var border = isFirst ? fgRgb : secRgb;
      var fill =
        type === 'line' &&
        (isFirst ? !!cfg.fill : !!cfg.fillSeries2);
      var fillColor = fill ? 'rgba(' + (isFirst ? fg : secTriplet) + ', 0.12)' : type === 'bar' ? 'rgba(' + (isFirst ? fg : secTriplet) + ', 0.35)' : border;

      return {
        label: meta.label,
        data: meta.data,
        borderColor: border,
        backgroundColor: fillColor,
        tension: type === 'line' ? 0.25 : 0,
        fill: type === 'line' ? !!fill : false,
        borderWidth: type === 'bar' ? 0 : 2,
        pointRadius: type === 'line' ? 4 : 0,
        pointHoverRadius: type === 'line' ? 6 : 0,
        borderDash: type === 'line' && !isFirst && !btn ? [7, 4] : undefined,
        spanGaps: false
      };
    });

    var chart = new Chart(ctx, {
      type: type,
      data: {
        labels: merged.labels,
        datasets: datasets
      },
      options: makeChartOptions(cfg, fg, fgRgb, type, datasets.length)
    });

    canvas.__busoGraphChart = chart;
  }

  function renderMultiSeriesFromPaired(cfg, canvas) {
    var seriesLabels = {
      1: cfg.series1Label || 'Series 1',
      2: cfg.series2Label || 'Series 2'
    };
    var merged = buildSeriesFromPairedPoints(cfg.pairedPoints, seriesLabels);
    if (!merged.labels.length || !merged.datasetsMeta.length) {
      var wrapEmpty = canvas.closest('.buso-graph__canvas-wrap');
      if (wrapEmpty) wrapEmpty.classList.add('buso-graph__canvas-wrap--empty');
      return;
    }

    canvas.dataset.busoGraphRendered = '1';
    var fg = foregroundFromCanvas(canvas);
    var fgRgb = 'rgb(' + fg + ')';
    var btn = buttonColorFromCanvas(canvas);
    var secRgb = btn ? 'rgb(' + btn + ')' : fgRgb;
    var secTriplet = btn || fg;

    var ctx = canvas.getContext('2d');
    var type = cfg.chartType === 'bar' ? 'bar' : 'line';

    var datasets = merged.datasetsMeta.map(function (meta, idx) {
      var isFirst = meta.key === '1' || idx === 0;
      var border = isFirst ? fgRgb : secRgb;
      var fill =
        type === 'line' &&
        (isFirst ? !!cfg.fill : !!cfg.fillSeries2);
      var fillColor = fill ? 'rgba(' + (isFirst ? fg : secTriplet) + ', 0.12)' : type === 'bar' ? 'rgba(' + (isFirst ? fg : secTriplet) + ', 0.35)' : border;

      return {
        label: meta.label,
        data: meta.data,
        borderColor: border,
        backgroundColor: fillColor,
        tension: type === 'line' ? 0.25 : 0,
        fill: type === 'line' ? !!fill : false,
        borderWidth: type === 'bar' ? 0 : 2,
        pointRadius: type === 'line' ? 4 : 0,
        pointHoverRadius: type === 'line' ? 6 : 0,
        borderDash: type === 'line' && !isFirst && !btn ? [7, 4] : undefined,
        spanGaps: false
      };
    });

    var chart = new Chart(ctx, {
      type: type,
      data: {
        labels: merged.labels,
        datasets: datasets
      },
      options: makeChartOptions(cfg, fg, fgRgb, type, datasets.length)
    });

    canvas.__busoGraphChart = chart;
  }

  function initAll() {
    var scripts = document.querySelectorAll('script[type="application/json"][data-buso-graph-config]');
    if (!scripts.length) return;

    loadChartJs()
      .then(function () {
        scripts.forEach(function (script) {
          if (script.dataset.busoGraphProcessed === '1') return;
          script.dataset.busoGraphProcessed = '1';
          var cfg = parseConfig(script);
          if (cfg) renderOne(cfg);
        });
      })
      .catch(function () {
        document.querySelectorAll('script[type="application/json"][data-buso-graph-config]').forEach(function (script) {
          var cfg = parseConfig(script);
          if (!cfg || !cfg.canvasId) return;
          var c = document.getElementById(cfg.canvasId);
          var wrap = c && c.closest('.buso-graph__canvas-wrap');
          if (wrap) wrap.classList.add('buso-graph__canvas-wrap--error');
        });
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
